import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
    UserCog, Plus, X, Edit2, ToggleLeft, ToggleRight, Trash2, Save, UserPlus,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { T } from '../ui/UI';

const POSITION_OPTIONS = ['CEO', 'Program Manager', 'Operations Manager', 'Project Manager', 'HR', 'Accounts', 'Site Engineer', 'Engineer', 'Supervisor', 'Foreman', 'Viewer', 'Coordination Team'];

// ✅ بدّلنا الستايل كله هنا من Tailwind + theme prop (gradient header
// slate، أزرار gradient أزرق/إنديجو، theme.card/theme.border بتبديل
// Tailwind dark:) لنفس الديزاين سيستم (jr-*) المستخدم في باقي الداشبورد
// (Add Task / Upload Document / New RFI / Add Stakeholder...) — خلفية
// var(--jr-surface) بدون أي gradient، حدود C.border، شريط لون علوي
// height:3، وpills/buttons بالألوان السيمانتيك (success/info/warning/
// danger) بدل الأزرق/الإنديجو الثابت. theme prop باقي مقبول في الدوال
// (العزم اللي بيبعته App.jsx) بس مش مستخدم تاني داخليًا.
const C = { bg: T.bg, card: T.surface, border: T.border, text: T.text, sub: T.text2, hover: T.surface2, input: T.surface2 };

const UserManagement = ({ onClose, projects, showToast, showConfirm }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingUser, setEditingUser] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newUser, setNewUser] = useState({
        email: '', password: '', name: '', position: 'Viewer',
        department: '', phone: '',
    });
    const [savingUser, setSavingUser] = useState(false);

    const allProjects = Object.values(projects);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- تحميل أولي عند mount فقط
    useEffect(() => { fetchUsers(); }, []);

    const fetchUsers = async () => {
        setLoading(true);
        // ✅ فحص error بعد الاستعلام (كانت غائبة قبل كده — أي فشل كان بيظهر كـ"لا يوجد مستخدمين" بصمت)
        const { data, error } = await supabase.from('user_profiles').select('*').order('name');
        if (error) {
            console.error('Fetch users error:', error);
            showToast('❌ Failed to load users. Please try again.');
        }
        setUsers(data || []);
        setLoading(false);
    };

    const updateUser = async (user) => {
        setSavingUser(true);
        try {
            const { error } = await supabase.from('user_profiles').update({
                name: user.name,
                position: user.position,
                department: user.department,
                phone: user.phone,
                is_active: user.is_active,
                assigned_projects: user.assigned_projects || [],
            }).eq('id', user.id);
            if (error) throw error;
            showToast('✅ User updated!');
            setEditingUser(null);
            fetchUsers();
        } catch (err) {
            console.error('Update user error:', err);
            showToast('❌ Failed to update user. Please try again.');
        }
        setSavingUser(false);
    };

    const toggleUserActive = async (user) => {
        const { error } = await supabase.from('user_profiles')
            .update({ is_active: !user.is_active })
            .eq('id', user.id);
        if (error) {
            console.error('Toggle user active error:', error);
            showToast('❌ Failed to update user. Please try again.');
            return;
        }
        showToast(user.is_active ? '🔴 User deactivated' : '🟢 User activated');
        fetchUsers();
    };

    const deleteUser = async (user) => {
        showConfirm(`Are you sure you want to delete ${user.name}? This cannot be undone.`, async () => {
            const { error } = await supabase.from('user_profiles').delete().eq('id', user.id);
            if (error) {
                console.error('Delete user error:', error);
                showToast('❌ Failed to delete user. Please try again.');
                return;
            }
            showToast('User deleted');
            fetchUsers();
        })
    };

    const addUser = async () => {
        if (!newUser.email || !newUser.name) {
            showToast('Please fill required fields');
            return;
        }
        setSavingUser(true);
        try {
            // بدل createUser — ابعت invite email
            const { error } = await supabase.auth.signInWithOtp({
                email: newUser.email,
                options: {
                    shouldCreateUser: true,
                    data: {
                        name: newUser.name,
                        position: newUser.position,
                        department: newUser.department,
                    }
                }
            });

            if (error) throw error;

            // أضف الـ profile مسبقاً
            const { error: profileError } = await supabase.from('user_profiles').insert([{
                username: newUser.email.split('@')[0],
                name: newUser.name,
                email: newUser.email,
                position: newUser.position,
                department: newUser.department || null,
                phone: newUser.phone || null,
                is_active: true,
                assigned_projects: [],
            }]);
            if (profileError) throw profileError;

            showToast('📧 Invite sent! User will receive a magic link.');
            setShowAddModal(false);
            setNewUser({ email: '', password: '', name: '', position: 'Viewer', department: '', phone: '' });
            fetchUsers();
        } catch (err) {
            console.error('Add user error:', err);
            showToast('❌ Failed to add user. Please try again.');
        }
        setSavingUser(false);
    };

    const positionOptions = POSITION_OPTIONS;

    // Pill tone per position — نفس منطق jr-pill-* المستخدم في باقي الداشبورد
    const positionTone = (position) =>
        ['CEO', 'Program Manager', 'Operations Manager', 'Project Manager'].includes(position) ? 'info'
            : (position === 'Supervisor' || position === 'Foreman') ? 'warning'
                : 'neutral';

    const inputStyle = { width: '100%', padding: '10px 13px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontSize: 13, fontWeight: 500, outline: 'none', boxSizing: 'border-box' };
    const labelStyle = { fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: C.sub, display: 'block', marginBottom: 6 };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ width: '100%', maxWidth: 1024, background: C.card, borderRadius: 24, overflow: 'hidden', border: `1px solid ${C.border}`, boxShadow: '0 32px 80px rgba(0,0,0,0.5)', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>

                {/* Header */}
                <div style={{ height: 3, background: T.accent }} />
                <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: T.accentSoft, border: `1px solid ${T.accentSoftBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <UserCog size={16} color={C.text} />
                        </div>
                        <div>
                            <h2 style={{ fontSize: 16, fontWeight: 900, color: C.text, letterSpacing: '-0.01em' }}>User Management</h2>
                            <p style={{ fontSize: 10, color: C.sub, fontWeight: 500 }}>{users.length} user{users.length !== 1 ? 's' : ''}</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setShowAddModal(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, background: T.accent, color: T.accentFg, border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                            <Plus size={16} />Add User
                        </button>
                        <button onClick={onClose} aria-label="Close"
                            style={{ width: 34, height: 34, borderRadius: 10, background: C.input, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: C.sub }}>
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Users list */}
                <div style={{ overflowY: 'auto', flex: 1, padding: 20 }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                            <div style={{ width: 32, height: 32, border: `3px solid ${T.border}`, borderTopColor: T.accent, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {users.map(user => (
                                <motion.div key={user.id} layout
                                    style={{
                                        borderRadius: 14, padding: '14px 16px',
                                        background: user.is_active === false ? T.dangerBg : C.hover,
                                        border: `1px solid ${user.is_active === false ? T.dangerBorder : C.border}`,
                                        opacity: user.is_active === false ? 0.75 : 1,
                                    }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                        {/* Avatar */}
                                        {user.avatar
                                            ? <img src={user.avatar} style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} alt="" />
                                            : <div style={{ width: 44, height: 44, borderRadius: 10, background: T.accentSoft, border: `1px solid ${T.accentSoftBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.text, fontWeight: 800, flexShrink: 0, fontSize: 13 }}>
                                                {user.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                            </div>
                                        }

                                        {/* Info */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <p style={{ fontWeight: 800, color: C.text, fontSize: 13, margin: 0 }}>{user.name}</p>
                                                <span className={`jr-pill jr-pill-${positionTone(user.position)}`}>{user.position}</span>
                                                {user.is_active === false && (
                                                    <span className="jr-pill jr-pill-danger">Inactive</span>
                                                )}
                                            </div>
                                            <p style={{ fontSize: 11, color: C.sub, marginTop: 3 }}>{user.email} {user.department && `• ${user.department}`}</p>
                                            {(user.assigned_projects || []).length > 0 && (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                                                    {(user.assigned_projects || []).map(pid => (
                                                        <span key={pid} className="jr-pill jr-pill-neutral">
                                                            {projects[pid]?.shortName || pid}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                            <button onClick={() => setEditingUser({ ...user })} title="Edit"
                                                style={{ width: 30, height: 30, borderRadius: 8, background: T.infoBg, border: `1px solid ${T.infoBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                                <Edit2 size={14} color={T.info} />
                                            </button>
                                            <button onClick={() => toggleUserActive(user)} title={user.is_active === false ? 'Activate' : 'Deactivate'}
                                                style={{ width: 30, height: 30, borderRadius: 8, background: user.is_active === false ? C.input : T.successBg, border: `1px solid ${user.is_active === false ? C.border : T.successBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                                {user.is_active === false
                                                    ? <ToggleLeft size={14} color={C.sub} />
                                                    : <ToggleRight size={14} color={T.success} />
                                                }
                                            </button>
                                            <button onClick={() => deleteUser(user)} title="Delete"
                                                style={{ width: 30, height: 30, borderRadius: 8, background: T.dangerBg, border: `1px solid ${T.dangerBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                                <Trash2 size={14} color={T.danger} />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Edit User Modal */}
                {editingUser && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, padding: 16 }}>
                        <div style={{ background: C.card, borderRadius: 20, padding: 24, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${C.border}`, boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                                <h4 style={{ fontSize: 15, fontWeight: 900, color: C.text, margin: 0 }}>Edit User</h4>
                                <button onClick={() => setEditingUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.sub, display: 'flex' }}><X size={18} /></button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                {[
                                    { label: 'Name', key: 'name' },
                                    { label: 'Department', key: 'department' },
                                    { label: 'Phone', key: 'phone' },
                                ].map(f => (
                                    <div key={f.key}>
                                        <label style={labelStyle}>{f.label}</label>
                                        <input type="text" value={editingUser[f.key] || ''}
                                            onChange={e => setEditingUser({ ...editingUser, [f.key]: e.target.value })}
                                            style={inputStyle} />
                                    </div>
                                ))}

                                {/* Position */}
                                <div>
                                    <label style={labelStyle}>Position</label>
                                    <select value={editingUser.position || 'Viewer'}
                                        onChange={e => setEditingUser({ ...editingUser, position: e.target.value })}
                                        style={{ ...inputStyle, cursor: 'pointer' }}>
                                        {positionOptions.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>

                                {/* Assigned Projects */}
                                <div>
                                    <label style={labelStyle}>Assigned Projects</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 160, overflowY: 'auto' }}>
                                        {allProjects.map(project => (
                                            <label key={project.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, border: `1px solid ${C.border}`, cursor: 'pointer', background: C.input }}>
                                                <input type="checkbox"
                                                    checked={(editingUser.assigned_projects || []).includes(project.id)}
                                                    onChange={e => {
                                                        const current = editingUser.assigned_projects || [];
                                                        const updated = e.target.checked
                                                            ? [...current, project.id]
                                                            : current.filter(p => p !== project.id);
                                                        setEditingUser({ ...editingUser, assigned_projects: updated });
                                                    }}
                                                    style={{ width: 15, height: 15, accentColor: T.accent }} />
                                                <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{project.shortName || project.name}</span>
                                                <span style={{ fontSize: 11, color: C.sub, marginLeft: 'auto' }}>{project.client}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                                <button onClick={() => setEditingUser(null)}
                                    style={{ flex: 1, padding: '11px', border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 700, background: 'none', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={() => updateUser(editingUser)} disabled={savingUser}
                                    style={{ flex: 1, padding: '11px', background: T.accent, color: T.accentFg, borderRadius: 10, fontWeight: 800, border: 'none', cursor: savingUser ? 'not-allowed' : 'pointer', opacity: savingUser ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                    {savingUser ? <div style={{ width: 14, height: 14, border: `2px solid ${T.accentFg}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> : <Save size={15} />}
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add User Modal */}
                {showAddModal && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, padding: 16 }}>
                        <div style={{ background: C.card, borderRadius: 20, padding: 24, width: '100%', maxWidth: 440, border: `1px solid ${C.border}`, boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                                <h4 style={{ fontSize: 15, fontWeight: 900, color: C.text, margin: 0 }}>Add New User</h4>
                                <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.sub, display: 'flex' }}><X size={18} /></button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                {[
                                    { label: 'Full Name *', key: 'name', type: 'text' },
                                    { label: 'Email *', key: 'email', type: 'email' },
                                    { label: 'Password *', key: 'password', type: 'password' },
                                    { label: 'Department', key: 'department', type: 'text' },
                                    { label: 'Phone', key: 'phone', type: 'tel' },
                                ].map(f => (
                                    <div key={f.key}>
                                        <label style={labelStyle}>{f.label}</label>
                                        <input type={f.type} value={newUser[f.key] || ''}
                                            onChange={e => setNewUser({ ...newUser, [f.key]: e.target.value })}
                                            style={inputStyle} />
                                    </div>
                                ))}

                                <div>
                                    <label style={labelStyle}>Position</label>
                                    <select value={newUser.position}
                                        onChange={e => setNewUser({ ...newUser, position: e.target.value })}
                                        style={{ ...inputStyle, cursor: 'pointer' }}>
                                        {positionOptions.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                                <button onClick={() => setShowAddModal(false)}
                                    style={{ flex: 1, padding: '11px', border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 700, background: 'none', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={addUser} disabled={savingUser}
                                    style={{ flex: 1, padding: '11px', background: T.accent, color: T.accentFg, borderRadius: 10, fontWeight: 800, border: 'none', cursor: savingUser ? 'not-allowed' : 'pointer', opacity: savingUser ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                    {savingUser ? <div style={{ width: 14, height: 14, border: `2px solid ${T.accentFg}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> : <UserPlus size={15} />}
                                    Create User
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserManagement;
